// ─── Registre d'icônes — Setal Express ────────────────────────────────────────
// Source unique de vérité pour toutes les icônes de l'application.
// Aucun emoji dans l'UI : on passe systématiquement par <Icon name="…" />.
//
// Trois usages :
//   1. <Icon name="order" />            — icône sémantique dans l'UI
//   2. resolveIcon(valeurBDD)           — convertit un emoji stocké en base
//   3. ARTICLE_ICONS                    — catalogue du sélecteur d'icônes admin

import React from "react";
import {
  Home, ShoppingBasket, MapPin, User, Users, Building2, Store, Package,
  ClipboardList, FileText, Receipt, Ticket, Calendar, CalendarDays, Clock,
  Hourglass, Timer, Phone, Smartphone, MessageCircle, Mail, Inbox, Bell,
  Megaphone, WifiOff, Globe, Map as MapIcon, Compass, Car, Truck, Bike,
  Handshake, Hand, Wallet, Banknote, CreditCard, Coins, PiggyBank, Landmark,
  BarChart3, TrendingUp, TrendingDown, Target, Trophy, Medal, Award, Gift,
  Star, Gem, PartyPopper, Lightbulb, Sparkles, Flame, GraduationCap,
  ShoppingCart, Circle, CheckCircle2, XCircle, AlertTriangle, AlertCircle,
  Info, HelpCircle, Check, CheckCheck, X, ArrowRight, ArrowLeft, Undo2, Menu, ChevronUp,
  ChevronDown, ChevronRight, Search, Pencil, Trash2, Save, Send, Upload,
  Download, RefreshCw, Repeat, Eye, EyeOff, Paperclip, Settings, Link2,
  Printer, Camera, Image as ImageIcon, Palette, Type, Square, Ban, Lock,
  LockOpen, ShieldCheck, Key, Plus, Minus, Filter, MoreHorizontal, LogOut,
  LayoutDashboard, List, Table2, Percent, Tag, Tags, QrCode, Copy,
  ExternalLink, Loader2, Wrench, UserPlus, UserCheck, Zap, FileSpreadsheet,
  Layers, Boxes, WashingMachine, Shirt, Footprints, Crown, Glasses, Watch,
  Briefcase, ShoppingBag, Backpack, Luggage, BedDouble, Sofa, Droplet,
  Droplets, Waves, Wind, Snowflake, Sun, Cloud, Flower2, Leaf, Recycle,
  Brush, SprayCan, Scissors, Ruler, Utensils, Blinds, Heart,
  Share2, Navigation,
} from "lucide-react";

// ─── Registre sémantique ──────────────────────────────────────────────────────
export const ICONS = {
  // Navigation
  home: Home, dashboard: LayoutDashboard, menu: Menu, back: ArrowLeft,
  forward: ArrowRight, logout: LogOut, external: ExternalLink, link: Link2,

  // Métier — commandes
  order: ShoppingBasket, cart: ShoppingCart, package: Package, delivery: Truck,
  collect: Car, scooter: Bike, track: MapPin, location: MapPin, map: MapIcon,
  compass: Compass, receipt: Receipt, ticket: Ticket, qr: QrCode,

  // Métier — pressing
  washing: WashingMachine, garment: Shirt, water: Droplet, waterDrops: Droplets,
  waves: Waves, air: Wind, clean: Sparkles, iron: Flame, fresh: Snowflake,
  recycle: Recycle, brush: Brush, spray: SprayCan,

  // Personnes
  user: User, users: Users, userAdd: UserPlus, userCheck: UserCheck,
  handshake: Handshake, hand: Hand,

  // Lieux
  building: Building2, store: Store, globe: Globe,

  // Documents
  document: FileText, list: ClipboardList, table: Table2, spreadsheet: FileSpreadsheet,
  layers: Layers, boxes: Boxes, attach: Paperclip, copy: Copy,

  // Temps
  calendar: Calendar, calendarDays: CalendarDays, clock: Clock,
  pending: Hourglass, timer: Timer,

  // Communication
  phone: Phone, mobile: Smartphone, chat: MessageCircle, whatsapp: MessageCircle,
  mail: Mail, inbox: Inbox, bell: Bell, announce: Megaphone, send: Send,
  offline: WifiOff,

  // Argent
  money: Wallet, cash: Banknote, card: CreditCard, coins: Coins,
  savings: PiggyBank, bank: Landmark, percent: Percent,

  // Statistiques
  chart: BarChart3, trendUp: TrendingUp, trendDown: TrendingDown, target: Target,

  // Récompenses
  trophy: Trophy, medal: Medal, award: Award, gift: Gift, star: Star,
  gem: Gem, celebrate: PartyPopper, idea: Lightbulb, fire: Flame,
  graduate: GraduationCap, heart: Heart, sparkle: Sparkles, bolt: Zap,

  // États
  success: CheckCircle2, error: XCircle, warning: AlertTriangle,
  alert: AlertCircle, info: Info, help: HelpCircle, check: Check,
  checkDouble: CheckCheck,
  close: X, dot: Circle, loading: Loader2,

  // Actions
  search: Search, filter: Filter, edit: Pencil, delete: Trash2, save: Save,
  upload: Upload, download: Download, refresh: RefreshCw, repeat: Repeat,
  undo: Undo2, add: Plus, remove: Minus, more: MoreHorizontal,
  settings: Settings, wrench: Wrench, print: Printer, camera: Camera,
  image: ImageIcon, palette: Palette, text: Type, stop: Square,
  share: Share2, navigate: Navigation,

  // Visibilité / sécurité
  show: Eye, hide: EyeOff, lock: Lock, unlock: LockOpen,
  shield: ShieldCheck, key: Key, forbidden: Ban,

  // Étiquettes
  tag: Tag, tags: Tags,

  // Chevrons
  chevronUp: ChevronUp, chevronDown: ChevronDown, chevronRight: ChevronRight,
};

// ─── Compatibilité : emojis déjà stockés en base ──────────────────────────────
// Les colonnes articles.icon / services.icon / expense_categories.icon
// contiennent historiquement des emojis. On les traduit à l'affichage,
// ce qui évite toute migration de données.
export const LEGACY_EMOJI = {
  // Pressing / entretien
  "🧺": "order", "🫧": "clean", "🧼": "clean", "🧽": "brush", "🪣": "waterDrops",
  "💧": "water", "💦": "waterDrops", "🚿": "waterDrops", "🌊": "waves",
  "♨️": "iron", "♨": "iron", "🧴": "spray", "🪥": "brush", "🫗": "water",
  "🌀": "refresh", "🔄": "refresh", "♻️": "recycle", "♻": "recycle",
  "🧹": "brush", "🪮": "brush", "🧪": "spray", "🧶": "garment",
  "🪡": "garment", "🧵": "garment",

  // Vêtements
  "👔": "garment", "👕": "garment", "👖": "garment", "👗": "garment",
  "👘": "garment", "🧥": "garment", "🤵": "garment", "👙": "garment",
  "👚": "garment", "🩳": "garment", "🩱": "garment", "🎽": "garment",
  "🩲": "garment", "🧦": "garment", "🧤": "garment", "🧣": "garment",

  // Accessoires
  "👒": "crown", "🧢": "crown", "🎩": "crown", "👑": "crown",
  "👞": "shoes", "👟": "shoes", "👠": "shoes", "🥿": "shoes", "👢": "shoes",
  "👜": "bag", "👝": "bag", "🎒": "backpack", "🧳": "luggage",
  "💍": "gem", "🕶️": "glasses", "🕶": "glasses", "⌚": "watch",

  // Maison
  "🛏️": "bed", "🛏": "bed", "🧸": "heart", "🍽️": "utensils", "🍽": "utensils",
  "🪟": "blinds", "🌸": "flower", "🌿": "leaf", "🎀": "gift",
  "☁️": "cloud", "☁": "cloud", "❄️": "fresh", "❄": "fresh",
  "🩵": "water", "💜": "gem", "🤍": "clean",

  // Valeur
  "✨": "clean", "💎": "gem", "⭐": "star", "🔥": "iron",
  "💫": "sparkle", "🌟": "star", "🪄": "sparkle",

  // Objets divers
  "📦": "package", "📋": "list", "📄": "document", "📃": "document",
  "🧾": "receipt", "🎟️": "ticket", "🎟": "ticket", "🎫": "ticket",
  "📅": "calendar", "📆": "calendarDays", "🕐": "clock", "⏰": "clock",
  "⏱️": "timer", "⏱": "timer", "⏳": "pending",

  // Personnes / lieux
  "👤": "user", "👥": "users", "🏢": "building", "🏪": "store",
  "🏠": "home", "🌐": "globe", "🌍": "globe", "🗺️": "map", "🗺": "map",
  "🧭": "compass",

  // Transport
  "🚗": "collect", "🚚": "delivery", "🛵": "scooter", "🏃": "collect",

  // Communication
  "📞": "phone", "📱": "mobile", "📲": "mobile", "💬": "chat",
  "📧": "mail", "✉️": "mail", "✉": "mail", "📨": "mail", "📭": "inbox",
  "🔔": "bell", "📢": "announce", "📡": "offline",

  // Argent
  "💰": "money", "💵": "cash", "💳": "card",

  // Statistiques
  "📊": "chart", "📈": "trendUp", "📉": "trendDown", "🎯": "target",

  // Récompenses
  "🏆": "trophy", "🏅": "medal", "🥇": "medal", "🥈": "medal", "🥉": "medal",
  "🎁": "gift", "🎉": "celebrate", "💡": "idea", "🎓": "graduate",
  "💝": "gift", "🤝": "handshake", "🙏": "heart", "👋": "hand",

  // États
  "✅": "success", "❌": "error", "⚠️": "warning", "⚠": "warning",
  "ℹ️": "info", "✓": "check", "✕": "close", "🚫": "forbidden",
  "🔴": "dot", "🟢": "dot", "🟡": "dot", "🟠": "dot", "🔵": "dot",

  // Actions
  "🔍": "search", "✏️": "edit", "✏": "edit", "✍️": "edit", "✍": "edit",
  "📝": "edit", "🗑️": "delete", "🗑": "delete", "💾": "save",
  "📤": "upload", "📥": "download", "🖨️": "print", "🖨": "print",
  "📷": "camera", "🖼️": "image", "🖼": "image", "🎨": "palette",
  "🔤": "text", "⚙️": "settings", "⚙": "settings", "🔗": "link",
  "📎": "attach", "👁️": "show", "👁": "show", "📂": "layers",
  "🔒": "lock", "🔓": "unlock", "🔐": "shield", "🔁": "repeat",
  "⏹️": "stop", "⏹": "stop", "🛒": "cart", "🆕": "add", "👆": "hand",
  "💻": "dashboard", "📘": "document", "🍩": "dot", "😕": "help",
  "☰": "menu", "▲": "chevronUp", "▼": "chevronDown",
  "→": "forward", "←": "back", "↩️": "undo", "↩": "undo",
};

// Icônes supplémentaires référencées par la table de compatibilité
Object.assign(ICONS, {
  shoes: Footprints, bag: ShoppingBag, backpack: Backpack, luggage: Luggage,
  bed: BedDouble, sofa: Sofa, utensils: Utensils, blinds: Blinds,
  flower: Flower2, leaf: Leaf, cloud: Cloud, sun: Sun, crown: Crown,
  glasses: Glasses, watch: Watch, briefcase: Briefcase, scissors: Scissors,
  ruler: Ruler,
});

/**
 * Convertit une valeur d'icône stockée en base vers un nom sémantique.
 * Accepte aussi bien un emoji hérité qu'un nom déjà migré.
 */
export function resolveIcon(value, fallback = "garment") {
  if (!value) return fallback;
  if (ICONS[value]) return value;              // déjà un nom sémantique
  const stripped = String(value).replace(/️/g, "");
  return LEGACY_EMOJI[value] || LEGACY_EMOJI[stripped] || fallback;
}

// ─── Composant Icon ───────────────────────────────────────────────────────────
/**
 * Icône standard de l'application.
 * Taille 16 et trait 1.5 par défaut, conformément à la direction « Clean Utility ».
 */
export function Icon({ name, size = 16, strokeWidth = 1.5, color, style, className, title }) {
  const Cmp = ICONS[name] || ICONS[resolveIcon(name, "help")] || HelpCircle;
  return (
    <Cmp
      size={size}
      strokeWidth={strokeWidth}
      color={color}
      className={className}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      style={{ flexShrink: 0, display: "block", ...style }}
    />
  );
}

/** Icône dont la valeur peut venir de la base (emoji hérité ou nom). */
export function DataIcon({ value, fallback = "garment", ...rest }) {
  return <Icon name={resolveIcon(value, fallback)} {...rest} />;
}

// ─── Catalogue du sélecteur d'icônes (admin) ──────────────────────────────────
// Remplace l'ancienne palette d'emojis. La valeur stockée est le nom sémantique.
export const ARTICLE_ICONS = [
  { name: "garment",    label: "Vêtement" },
  { name: "shoes",      label: "Chaussures" },
  { name: "bag",        label: "Sac" },
  { name: "backpack",   label: "Sac à dos" },
  { name: "luggage",    label: "Valise" },
  { name: "crown",      label: "Chapeau" },
  { name: "glasses",    label: "Lunettes" },
  { name: "watch",      label: "Montre" },
  { name: "gem",        label: "Bijou" },
  { name: "briefcase",  label: "Mallette" },
  { name: "bed",        label: "Literie" },
  { name: "sofa",       label: "Ameublement" },
  { name: "blinds",     label: "Rideaux" },
  { name: "utensils",   label: "Nappe & linge de table" },
  { name: "washing",    label: "Lavage machine" },
  { name: "water",      label: "Lavage" },
  { name: "waterDrops", label: "Trempage" },
  { name: "waves",      label: "Rinçage" },
  { name: "spray",      label: "Détachage" },
  { name: "brush",      label: "Brossage" },
  { name: "iron",       label: "Repassage" },
  { name: "air",        label: "Séchage" },
  { name: "fresh",      label: "Traitement délicat" },
  { name: "clean",      label: "Nettoyage à sec" },
  { name: "recycle",    label: "Écologique" },
  { name: "flower",     label: "Parfumage" },
  { name: "leaf",       label: "Naturel" },
  { name: "package",    label: "Colis" },
  { name: "gift",       label: "Emballage cadeau" },
  { name: "star",       label: "Premium" },
];

export default Icon;
